import { useCallback, useEffect, useState } from "react";
import { Radio, ChevronRight, Users } from "lucide-react";
import api, { getAllPages } from "../services/api";
import { connectAdminSocket, getAdminSocket } from "../services/adminSocket";
import RefreshButton from "./RefreshButton";
import { timeAgo } from "../utils/time";
import { cancellationLine } from "../utils/tripLog";

const STATUS_PILL = {
  requested: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  accepted: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  in_progress: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
  awaiting_payment: "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  cancelled: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};


const bookingToRow = (b) => ({
  bookingId: String(b._id),
  passengerName: b.passengerId?.name || "Customer",
  pickup: b.pickupLocationName,
  dropoff: b.dropoffLocationName,
  seats: b.seats || 1,
  isCharter: b.isCharter || false,
  driversNotified: b.meta?.notifiedDriverIds?.length ?? null,
  status: b.status,
  createdAt: b.createdAt,
  cancellation: cancellationLine(b),
});

/**
 * Live feed of ride requests: the newest bookings plus every ride still waiting
 * for a rider, with `admin:ride_requested` socket events added in real time.
 * Clicking a row picks it for the dispatch panel beside it.
 */
function LiveRideMonitor({ selectedId = null, onSelect, onWaitingCount }) {
  const [rows, setRows] = useState([]);
  const [live, setLive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState(false);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      // Every waiting ride, however old, so none drops out of reach of dispatch.
      const [recent, waiting] = await Promise.all([
        api.get("/v1/admin/bookings", { params: { limit: 20 } }),
        getAllPages("/v1/admin/bookings", { status: "requested" }),
      ]);
      const list = [...(Array.isArray(recent.data?.data) ? recent.data.data : []), ...waiting];
      // The server is the source of truth: its rows replace what was on screen.
      const byId = new Map(list.map((b) => [String(b._id), bookingToRow(b)]));
      setRows(
        Array.from(byId.values()).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        ),
      );
      setUpdatedAt(new Date());
      setError(false);
    } catch {
      // Keep the last-known rows and say the refresh failed.
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    onWaitingCount?.(rows.filter((r) => r.status === "requested").length);
  }, [rows, onWaitingCount]);

  useEffect(() => {
    loadBookings();
    const socket = connectAdminSocket() || getAdminSocket();
    if (socket) {
      setLive(socket.connected);
      const onConnect = () => setLive(true);
      const onDisconnect = () => setLive(false);
      const onRequested = (p) => {
        if (!p?.bookingId) return;
        setRows((prev) => {
          const row = {
            bookingId: String(p.bookingId),
            passengerName: p.passengerName || "Customer",
            pickup: p.pickupLocationName,
            dropoff: p.dropoffLocationName,
            seats: p.seats || 1,
            isCharter: p.isCharter || false,
            driversNotified: p.driversNotified ?? null,
            status: "requested",
            createdAt: p.createdAt || new Date().toISOString(),
          };
          const rest = prev.filter((r) => r.bookingId !== row.bookingId);
          return [row, ...rest].slice(0, 40);
        });
        setLive(true);
      };
      const onUpdate = () => loadBookings();

      socket.on("connect", onConnect);
      socket.on("disconnect", onDisconnect);
      socket.on("admin:ride_requested", onRequested);
      socket.on("admin:ride_update", onUpdate);

      const poll = setInterval(loadBookings, 30000);
      return () => {
        socket.off("connect", onConnect);
        socket.off("disconnect", onDisconnect);
        socket.off("admin:ride_requested", onRequested);
        socket.off("admin:ride_update", onUpdate);
        clearInterval(poll);
      };
    }
    const poll = setInterval(loadBookings, 30000);
    return () => clearInterval(poll);
  }, [loadBookings]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col lg:h-[560px]">
      <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Live ride monitor
          </h3>
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${
              live
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                : "bg-gray-100 text-gray-500 dark:bg-gray-800"
            }`}
          >
            <Radio className={`w-3 h-3 ${live ? "animate-pulse" : ""}`} />
            {live ? "live" : "offline"}
          </span>
        </div>
        <RefreshButton onRefresh={loadBookings} loading={loading} updatedAt={updatedAt} />
      </div>
      {error ? (
        <p className="mx-5 mt-3 text-xs rounded-lg bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300 px-3 py-2">
          {"Couldn't refresh. Showing the last loaded rides. "}
          <button type="button" onClick={loadBookings} className="underline font-medium">
            Retry
          </button>
        </p>
      ) : null}

      <div className="flex-1 lg:overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/60">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            Waiting for ride requests…
          </div>
        ) : (
          rows.map((r) => (
            <button
              key={r.bookingId}
              onClick={() => onSelect?.(r.bookingId, r)}
              aria-pressed={selectedId === r.bookingId}
              className={`w-full text-left px-5 py-3 flex items-center gap-3 transition-colors border-l-2 ${
                selectedId === r.bookingId
                  ? "bg-orange-50 border-[#FF8C00] dark:bg-orange-500/10"
                  : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {r.passengerName}
                  </span>
                  <span
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_PILL[r.status] || "bg-gray-100 text-gray-600"}`}
                  >
                    {String(r.status).replace(/_/g, " ")}
                  </span>
                  {r.isCharter ? (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                      charter
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {r.pickup} → {r.dropoff}
                </p>
                {r.cancellation ? (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5" title={r.cancellation}>
                    {r.cancellation}
                  </p>
                ) : null}
                <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400">
                  <span>{timeAgo(r.createdAt)}</span>
                  {r.driversNotified != null ? (
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {r.driversNotified} notified
                    </span>
                  ) : null}
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 shrink-0 ${selectedId === r.bookingId ? "text-[#FF8C00]" : "text-gray-300"}`} />
            </button>
          ))
        )}
      </div>

    </div>
  );
}

export default LiveRideMonitor;
